import React from "react";
import { RevealedDocument, ScenarioDocument, Persona } from "../types";
import { Icons } from "./Icon";
import { downloadFile } from "../utils/fileUtils";
import PersonaAvatar from "./PersonaAvatar";

interface FileDrawerProps {
  documents: ScenarioDocument[];
  revealedDocs: RevealedDocument[];
  personas: Persona[];
}

const FileDrawer: React.FC<FileDrawerProps> = ({
  documents,
  revealedDocs,
  personas,
}) => {
  const getDoc = (id: string) => documents.find((d) => d.id === id);
  const getPersona = (id: string) => personas.find((p) => p.id === id);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-4 border-b border-outline-variant/10">
        <h3 className="text-xs font-label font-bold text-slate-400 tracking-[0.2em] uppercase flex items-center gap-2">
          <Icons.Archive className="text-accent w-4 h-4" />
          Documents
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {revealedDocs.length === 0 ? (
            <div className="text-center text-gray-500 mt-10 px-6">
              <Icons.FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No files collected yet.</p>
              <p className="text-xs mt-2">
                Ask personas for the documents they hold.
              </p>
            </div>
          ) : (
            revealedDocs.map((item, idx) => {
              const doc = getDoc(item.documentId);
              const persona = getPersona(item.revealedByPersonaId);
              if (!doc) return null;

              return (
                <div
                  key={`${item.documentId}-${idx}`}
                  className="bg-slate-800 p-4 rounded-lg border border-slate-700 hover:border-accent/50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-200">{doc.name}</h3>
                    </div>
                    <button
                      onClick={() => downloadFile(doc)}
                      className="text-primary hover:text-blue-400"
                      title="Open"
                    >
                      <Icons.Download size={18} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                    {doc.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 border-t border-slate-700 pt-2">
                    {persona && (
                      <PersonaAvatar
                        name={persona.name}
                        src={persona.avatar}
                        className="w-4 h-4 rounded-full"
                        textClassName="text-[7px]"
                      />
                    )}
                    <span>Provided by {persona?.name || "Unknown"}</span>
                    <span className="ml-auto opacity-50">
                      {new Date(item.revealedAt).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
    </div>
  );
};

export default FileDrawer;
